import {MenuContext, MenuStore} from './components/popup/context';
import {HeaderContext, HeaderStore} from './components/header/context';
import {GlobalTheme} from '@carbon/react';
import {BrowserRouter, Route, Routes} from 'react-router-dom';
import LandingPage from './pages/landing';
import UILibraryPage from './pages/misc/ui-library';
import AttributionPage from './pages/misc/attribution';
import WhatsNewPage from './pages/whats-new';
import TestPage from './pages/misc/test';
import DownloadingPage from './pages/misc/downloading';
import AuthPage from './pages/user/auth';
import AuthReceiverPage from './pages/user/auth-receiver';
import LogInPage from './pages/user/login';
import PasswordResetPage from './pages/user/password-reset';
import ChangePasswordPage from './pages/user/change-password';
import JoinPage from './pages/user/join';
import ActivityTrackerIframe from './pages/activity-tracker-iframe';
import InsightContainerViewPage from './pages/insight-container';
import UserOrgsPage from './pages/user/orgs';
import ManageMember from './pages/org/manage-member';
import InviteMember from './pages/org/invite-member';
import UserPage from './pages/user';
import CanvasViewPage from './pages/canvas';
import {CanvasLandingPage} from './pages/canvas/landing';
import CanvasRedirect from './pages/canvas/canvas-redirect';
import VisBuilderPage from './insight-container/vis-builder';
import SearchPage from './pages/search';
import ToolDiscoveryPage from './pages/tools/tool-discovery';
import SiteAdminPage from './pages/site-admin';
import OrgMembership from './pages/org/membership';
import DataExplorerPage from './pages/tools/data-explorer';
import DataDictionaryPage from './pages/data-dictionary';
import HCIndexListPage from './pages/tools/hc-index/list/list';
import HCIndexViewPage from './pages/tools/hc-index/view';
import LookupDrgPage from './pages/tools/drg-tool/table-view';
import CRBenchmarks from './pages/tools/cr-benchmarks';
import CRBOverviewPage from './pages/tools/cr-benchmarks/overview';
import CommonCoreBenchmarkPage from './pages/tools/common-core';
import CommonCoreOverviewPage from './pages/tools/common-core/overview';
import CrosswalkBuilderPage from './pages/tools/crosswalk-builder';
import AffiliationExplorerPage from './pages/tools/affiliation-explorer';
import HPTInstitutionalTool from './pages/tools/hpt-medicare-relativity/institutional-tool';
import HPTProfessionalTool from './pages/tools/hpt-medicare-relativity/professional-tool';
import HPTOverviewPage from './pages/tools/hpt-medicare-relativity/overview';
import HCGCostModelPage from './pages/tools/hcg-cost-model';
import NetworkOptimizationViewPage from './pages/network-optimization';
import MyFiles from './pages/network-optimization/pages/myFiles';
import MyFilesNew from './pages/network-optimization/pages/myFilesNew';
import {RosterIDWrapper} from './pages/network-optimization/pages/roster-id-wrapper';
import NetworkAnalysisPage from './pages/network-optimization/pages/networkAnalysisPage';
import NetworkAccessSummaryPage from './pages/network-optimization/pages/networkAccessSummaryPage';
import ReportsPage from './pages/network-optimization/pages/reports-page';
import APIDocumentation from './pages/documentation/api';
import CrmApiDocumentation from './pages/documentation/crm-api';
import NotFoundPage from './pages/misc/not-found';
import PopupMenu from './components/popup/menu';
import LogInRedirectPage from './pages/user/login-redirect';
import NetworkOptimizationLoginPage from './pages/user/network-optimization-login';
import RosterComparisonPage from './pages/network-optimization/pages/rosterComparisonPage';
import {RevisionIDWrapper} from './pages/network-optimization/pages/revision-id-wrapper';
import GenerateReport from './pages/network-optimization/pages/generate-report';

// Set to true to enable the new My Files page (kept off until roster ingestion is ready for production)
export const MY_FILES_NEW_ENABLED = false;

export default function App() {
    return (
        <MenuContext.Provider value={MenuStore.empty().setup()}>
            <HeaderContext.Provider value={HeaderStore.empty()}>
                <GlobalTheme theme={'g10'}>
                    <BrowserRouter>
                        <Routes>
                            <Route path="/" element={<LandingPage />} />
                            <Route path="/ui-library" element={<UILibraryPage />} />
                            <Route path="/attribution" element={<AttributionPage />} />
                            <Route path="/whats-new" element={<WhatsNewPage />} />
                            <Route path="/test" element={<TestPage />} />
                            <Route path="/file-download/:id" element={<DownloadingPage />} />

                            <Route path="/ic/:id" element={<InsightContainerViewPage />} />

                            <Route path="/user/orgs" element={<UserOrgsPage />} />
                            <Route path="/user/orgs/:id" element={<UserOrgsPage />} />
                            <Route path="/org/:id/membership/:userId" element={<ManageMember />} />
                            <Route path="/org/:orgId/invite" element={<InviteMember />} />
                            <Route path="/user/*" element={<UserPage />} />

                            <Route path="/canvas/:id/:section" element={<CanvasViewPage />} />
                            <Route path="/canvas" element={<CanvasLandingPage />} />
                            <Route path="/profile/:title" element={<CanvasViewPage />} />
                            <Route path="/canvas-redirect/:dataset/:value" element={<CanvasRedirect />} />

                            <Route path="/vis-builder/:id" element={<VisBuilderPage />} />

                            <Route path="/search" element={<SearchPage />} />

                            {/*Tool Discovery*/}
                            <Route path="/tools" element={<ToolDiscoveryPage />} />

                            {/*Site Admin*/}
                            <Route path="/site-admin/*" element={<SiteAdminPage />} />

                            {/*Org Admin*/}
                            <Route path="/org/:id/membership" element={<OrgMembership />} />

                            {/* Data Explorer */}
                            <Route path="/data/explore" element={<DataExplorerPage />} />
                            <Route path="/data/:id" element={<DataExplorerPage />} />

                            <Route path="/data-dictionary" element={<DataDictionaryPage />} />

                            {/* HC Index */}
                            <Route path="/healthcare-index" element={<HCIndexListPage />} />
                            <Route path="/healthcare-index/:key" element={<HCIndexViewPage />} />
                            <Route path="/healthcare-index/:key/:queryId" element={<HCIndexViewPage />} />

                            <Route path="/drg/table" element={<LookupDrgPage />} />
                            <Route path="/cr-benchmarks" element={<CRBenchmarks />} />
                            <Route path="/cr-benchmarks/overview" element={<CRBOverviewPage />} />
                            <Route path="/cr-benchmarks/:region/:reportType/:metricType" element={<CRBenchmarks />} />
                            <Route path="/cr-benchmarks/:region/:reportType/:metricType/:qualifier" element={<CRBenchmarks />} />

                            <Route path="/common-core" element={<CommonCoreBenchmarkPage />} />
                            <Route path="/common-core/overview" element={<CommonCoreOverviewPage />} />
                            <Route path="/common-core/:key" element={<CommonCoreBenchmarkPage />} />

                            <Route path="/crosswalk-builder" element={<CrosswalkBuilderPage />} />
                            <Route path="/crosswalk-builder/:from/:to/:type" element={<CrosswalkBuilderPage />} />
                            <Route path="/affiliations" element={<AffiliationExplorerPage />} />
                            <Route path="/affiliations/:from/:to/:type" element={<AffiliationExplorerPage />} />

                            <Route path="/hpt/medicare-relativity-score" element={<HPTInstitutionalTool />} />
                            <Route path="/hpt/professional/medicare-relativity-score" element={<HPTProfessionalTool />} />
                            <Route path="/hpt/medicare-relativity-score/overview" element={<HPTOverviewPage />} />

                            <Route path="/hcg_cost_model" element={<HCGCostModelPage />} />

                            {/* Network Optimzation */}
                            <Route path="/network-optimization" element={<NetworkOptimizationViewPage />}>
                                <Route path="my-files" element={<MyFiles />} />
                                {MY_FILES_NEW_ENABLED && <Route path="my-files-new" element={<MyFilesNew />} />}
                                <Route path=':rosterId' element={<RosterIDWrapper />} >
                                    <Route path=':revisionId' element={<RevisionIDWrapper />} >
                                        <Route path="network-analysis" element={<NetworkAnalysisPage />} />
                                        <Route path="network-access-summary" element={<NetworkAccessSummaryPage />} />
                                        <Route path="reports" element={<ReportsPage />} />
                                        <Route path="generate-report" element={<GenerateReport />} />
                                        <Route path="compare" element={<RosterComparisonPage />} />
                                    </Route>
                                </Route>
                            </Route>

                            {/* Documentation */}
                            <Route path="/docs/api/*" element={<APIDocumentation />} />
                            <Route path="/docs/crm-api/*" element={<CrmApiDocumentation />} />

                            <Route path="/auth" element={<AuthPage />} />
                            <Route path="/auth-receiver" element={<AuthReceiverPage />} />
                            <Route path="/login" element={<LogInPage />} />
                            <Route path="/network-optimization/login" element={<NetworkOptimizationLoginPage />} />
                            <Route path="/reset-password" element={<PasswordResetPage />} />
                            <Route path="/change-password" element={<ChangePasswordPage />} />
                            <Route path="/join/:code" element={<JoinPage />} />
                            <Route path="/track-activity" element={<ActivityTrackerIframe />} />
                            <Route path="/login-redirect" element={<LogInRedirectPage />} />

                            <Route element={<NotFoundPage />} />
                        </Routes>
                        <PopupMenu />
                    </BrowserRouter>
                </GlobalTheme>
            </HeaderContext.Provider>
        </MenuContext.Provider>
    );
}